import _ from 'lodash';
import { action, extendObservable, observable } from 'mobx';
import type {
  DayNote,
  Schedule,
  ScheduleAppointment,
  ScheduleBlock,
  ScheduleResponse,
  ScheduleSession
} from 'tm3-core/api.dtos';
import { MonthlyScheduleResponse } from 'tm3-core/api.dtos';

export type TMode = 'appointments' | 'sessions';

export interface IMergable<T> {
  onMerge(value: T): void;
}

export class BlockStore {
  @observable count = 0;
  @observable holiday = false;
  @observable readonly keys = observable.array<string>([]);
  @observable readonly resources = observable.map<string, ResourceStore>(new Map());
  @observable readonly monthlySummary = observable.object({});

  @action.bound
  onMerge(props: ScheduleBlock): void {
    this.count = props.count;
    this.holiday = props.holiday;
    this.keys.replace(props.keys);
    this.mergeResources(props.keys, props.values);
  }

  @action.bound
  private mergeResources(keys: string[], newResources: Record<string, Schedule>): void {
    const { resources } = this;
    keys.forEach((key, i) => {
      const lResorces = resources.has(key) ? resources : resources.set(key, new ResourceStore());
      lResorces.get(key)!.onMerge(newResources[key]);
    });
  }
}

export class ResourceStore {
  @observable id = 0;
  @observable name = '';
  @observable readonly sessions = observable.map<string, EventStore<ScheduleSession>>(new Map());
  @observable readonly appointments = observable.map<string, EventStore<ScheduleAppointment>>(
    new Map()
  );
  @observable dayNote: DayNote | undefined = undefined;

  @action.bound
  onMerge(props: Schedule): void {
    this.id = props.id;
    this.name = props.name;
    this.dayNote = props.dayNote;
    this.mergeAppointments(props.appointments);
    this.mergeSessions(props.sessions);
  }

  @action.bound
  private mergeSessions(newSessions: Record<string, ScheduleSession>): void {
    const { sessions } = this;
    Object.entries(newSessions).forEach(([key, value]) => {
      const lSessions = sessions.has(key) ? sessions : sessions.set(key, new EventStore());
      lSessions.get(key)!.onMerge(value);
    });
  }

  @action.bound
  private mergeAppointments(newAppointments: Record<string, ScheduleAppointment>): void {
    const { appointments } = this;
    Object.entries(newAppointments).forEach(([key, value]) => {
      const lAppointments = appointments.has(key)
        ? appointments
        : appointments.set(key, new EventStore());
      lAppointments.get(key)!.onMerge(value);
    });
  }
}

export class EventStore<T extends {}> {
  @action.bound
  onMerge(props: T) {
    const keys = Object.keys(this);
    if (!!keys.length) {
      const rest: Partial<T> = {};
      Object.keys(props).map((key) => {
        if (keys.includes(key)) {
          (this as unknown as T)[key as keyof T] = props[key as keyof T];
        } else {
          rest[key as keyof T] = props[key as keyof T];
        }
      });
      extendObservable(this, rest);
    } else {
      extendObservable(this, props);
    }
  }
}

export class ScheduleStore {
  @observable count = 0;
  @observable columns = 0;
  @observable readonly keys = observable.array<string>([]);
  @observable readonly blocks = observable.map<string, BlockStore>(new Map());
  // Bumped on every session merge so consumers that derive from the set of
  // sessions (e.g. the diary SVG pattern <defs> fetched from PatternService,
  // which emits one `restrictions-{id}` entry per restricted working time)
  // can re-fetch when a new/updated session is added. Appointments do not
  // contribute to patterns so they are intentionally excluded.
  @observable sessionMutationVersion = 0;

  @action.bound
  onLoad(res: ScheduleResponse): void {
    this.count = res.count;
    this.columns = res.columns;
    this.keys.replace(res.keys);

    const { blocks } = this;
    blocks.clear();
    res.keys.forEach((key) => {
      const lBlocks = blocks.has(key) ? blocks : blocks.set(key, new BlockStore());
      lBlocks.get(key)!.onMerge(res.blocks[key]);
    });
  }

  getBlock(block: number): BlockStore | undefined {
    if (block >= 0 && block < this.keys.length) return this.blocks.get(this.keys[block]);
    return undefined;
  }

  getBlockByKey(key: string): BlockStore | undefined {
    return this.blocks.get(key);
  }

  getResource(block: number, resource: number): ResourceStore | null {
    const res = this.getBlock(block);
    //hack for inverted render
    //remove when fixed
    if (!res) {
      return null;
    }
    const keys = res.keys;
    if (resource >= keys.length) {
      return null;
    }
    return res.resources.get(keys[resource]) ?? null;
  }

  getResourceByKey(blockKey: string, resourceKey: string): ResourceStore | null {
    const res = this.getBlockByKey(blockKey);
    if (!res) {
      return null;
    }
    //hack for inverted render
    //remove when fixed
    const val = res.resources.get(resourceKey);
    if (!val) {
      return null;
    }
    return val;
  }

  private _onChangeMap(obj, key: string) {
    const map = {};

    Object.keys(obj).forEach((blockId) => {
      const block = obj[blockId];
      block.forEach((evt) => {
        map[evt.scheduleId] = { block: blockId, resource: evt[key] };
      });
    });

    return map;
  }

  //This method needs modded to be more efficient
  @action.bound
  onMerge(mode: TMode, key: string, obj) {
    if (mode === 'sessions') {
      this.sessionMutationVersion += 1;
    }
    const map = this._onChangeMap(obj, key);

    this.keys.forEach((blockId) => {
      const block = this.getBlockByKey(blockId);
      if (!block) return;

      const group = _.groupBy<ScheduleSession & ScheduleAppointment>(obj[blockId], key);
      let b: Set<String>;
      block.keys.forEach((resId) => {
        const resource = this.getResourceByKey(blockId, resId)?.[mode];
        if (!resource) return;

        const a = new Set(resource.keys());
        b = b ?? new Set(Object.keys(map));
        const intersection = new Set([...a].filter((x) => b.has(x)));

        intersection.forEach((x) => {
          if (map[x].block === blockId && map[x].resource === resId) {
            return;
          }
          resource.delete(x);
        });

        let evts: Record<string, EventStore<ScheduleAppointment> | EventStore<ScheduleSession>> =
          {};
        group[resId]?.forEach((x: ScheduleSession & ScheduleAppointment) => {
          const evt = resource.get(x.scheduleId) ?? new EventStore();
          evt.onMerge(x);

          evts[x.scheduleId] = evt;
        });
        resource.merge(evts);
      });
    });
  }

  // @action
  // onMerge(mode, key, obj) {
  //   this.keys.forEach((block) => {
  //     if (!obj[block]) {return}
  //     let group = _.groupBy(obj[block], key)
  //     Object.keys(group).forEach((resId) => {
  //       let list = this.getResourceByKey(block, resId).get(mode)
  //       group[resId].map((x) => {
  //         list.set(x.scheduleId, x)
  //       })
  //     })
  //   })
  // }

  @action
  onRemove(mode: TMode, key: string, obj): void {
    if (!obj) {
      return;
    }

    this.keys.forEach((block) => {
      if (!obj[block]) {
        return;
      }
      const group = _.groupBy(obj[block], key);
      Object.keys(group).forEach((resId) => {
        const list = this.getResourceByKey(block, resId)?.[mode];
        group[resId].forEach((x) => {
          list?.delete(x.scheduleId);
        });
      });
    });
  }
}

const GlobalScheduleStore = new ScheduleStore();

export default GlobalScheduleStore;
